/* Minori · dati storici.
 *
 * Tre cose che questa schermata deve dire e che il prototipo non poteva sapere:
 *
 * 1. L'elenco viene da una cache su disco ricalcolata da un cron ogni due ore:
 *    non è lo stato del database adesso. L'ora dell'ultimo ricalcolo va scritta,
 *    altrimenti un download appena finito sembra non essere avvenuto.
 * 2. Due righe con la stessa coppia non sono un errore: un buco di più di
 *    sessanta secondi spezza il periodo in due range distinti. È esattamente il
 *    dato che serve prima di lanciare un backtest lungo.
 * 3. `POST /historical-data/fetch-data` è SINCRONO e senza avanzamento: su anni
 *    di storia la richiesta resta appesa per minuti. Va detto prima, non dopo.
 *
 * Il conteggio delle candele e il peso su disco del prototipo non esistono
 * nella risposta: al loro posto c'è la durata coperta, che è un dato vero.
 * La richiesta è annotata in RICHIESTE-API.md.
 */

import { useMemo, useState } from 'react';
import { Avvertenza, Distintivo, Pulsante, Scheda, Selettore } from '../../componenti';
import { AzioneDistruttiva } from '../../componenti/AzioneDistruttiva';
import { Campo } from '../../componenti/Modulo';
import { Modale, usaAvvisi, Avvisi } from '../../componenti/Sovrapposizioni';
import { ContenitoreDati, ScheletroLista } from '../../componenti/Stati';
import { datiStorici, exchanges } from '../../api/risorse';
import { ErroreApi } from '../../api/client';
import { usaRichiesta } from '../../api/usaRichiesta';
import { daQuanto, dataOra, durataPeriodo, num } from '../../lib/formato';
import type { RangeDatiStorici } from '../../api/tipi';
import type { Tono } from '../../lib/metriche';
import './minori.css';

/** Oltre questi giorni dalla fine del range, i dati non bastano più per un
 *  backtest che pretenda di dire qualcosa sull'oggi. */
const GIORNI_IN_RITARDO = 2;
const GIORNI_VECCHIO = 8;

interface Freschezza {
  testo: string;
  tono: Tono;
  giorni: number;
}

function freschezza(fine: string): Freschezza {
  const giorni = Math.max(0, Math.round((Date.now() - new Date(fine).getTime()) / 86_400_000));
  if (giorni <= GIORNI_IN_RITARDO) return { testo: 'aggiornato', tono: 'neutro', giorni };
  if (giorni <= GIORNI_VECCHIO) return { testo: `in ritardo di ${giorni} giorni`, tono: 'cautela', giorni };
  return { testo: `fermo da ${giorni} giorni`, tono: 'cautela', giorni };
}

export function DatiStorici() {
  const { avvisi, mostra } = usaAvvisi();
  const [apreScarico, setApreScarico] = useState(false);
  const [daEliminare, setDaEliminare] = useState<RangeDatiStorici | null>(null);
  const [inEliminazione, setInEliminazione] = useState(false);
  const [inRicalcolo, setInRicalcolo] = useState(false);

  const schema = usaRichiesta((segnale) => datiStorici.schema(segnale), []);
  const stato = usaRichiesta(() => datiStorici.statoSchema(), []);
  const listaExchange = usaRichiesta(() => exchanges.elenco({ sort: ['name,ASC'] }), []);

  const righe = useMemo(
    () =>
      [...(schema.dati ?? [])].sort((a, b) =>
        `${a.asset}${a.currency}`.localeCompare(`${b.asset}${b.currency}`),
      ),
    [schema.dati],
  );

  /* Una coppia con più di un range ha dei buchi: è il dato che decide se un
     backtest lungo è affidabile, quindi si conta e si dice. */
  const coppieSpezzate = useMemo(() => {
    const conto = new Map<string, number>();
    for (const r of righe) {
      const k = `${r.exchangeId}·${r.asset}/${r.currency}`;
      conto.set(k, (conto.get(k) ?? 0) + 1);
    }
    return [...conto.values()].filter((n) => n > 1).length;
  }, [righe]);

  async function ricalcola() {
    setInRicalcolo(true);
    try {
      const esito = await datiStorici.aggiornaSchema();
      mostra(
        esito.alreadyRunning
          ? 'Il ricalcolo era già in corso: l’elenco si aggiorna da solo.'
          : 'Ricalcolo avviato: l’elenco si aggiorna quando finisce.',
        'neutro',
      );
      schema.ricarica();
      stato.ricarica();
    } catch (e) {
      mostra(e instanceof ErroreApi ? e.message : 'Ricalcolo non riuscito.', 'fallito');
    } finally {
      setInRicalcolo(false);
    }
  }

  async function elimina() {
    if (!daEliminare) return;
    setInEliminazione(true);
    try {
      const cancellate = await datiStorici.elimina({
        exchangeId: daEliminare.exchangeId,
        asset: daEliminare.asset,
        currency: daEliminare.currency,
      });
      mostra(
        `${num(cancellate)} candele cancellate per ${daEliminare.asset}/${daEliminare.currency}.`,
        'riuscito',
      );
      setDaEliminare(null);
      schema.ricarica();
    } catch (e) {
      mostra(e instanceof ErroreApi ? e.message : 'Cancellazione non riuscita.', 'fallito');
    } finally {
      setInEliminazione(false);
    }
  }

  return (
    <div className="sb-colonna">
      <Avvisi avvisi={avvisi} />

      <div className="sb-riga">
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontWeight: 'var(--sb-peso-semi)', fontSize: 'var(--sb-t-14)' }}>
            Dati storici
          </div>
          <div className="sb-nota" style={{ marginTop: 'var(--sb-s-2)' }}>
            i backtest possono coprire solo i periodi presenti qui
          </div>
        </div>
        <Pulsante variante="tenue" onClick={ricalcola} disabled={inRicalcolo || stato.dati?.refreshing}>
          {inRicalcolo || stato.dati?.refreshing ? 'Ricalcolo in corso…' : 'Ricalcola l’elenco'}
        </Pulsante>
        <Pulsante variante="primario" onClick={() => setApreScarico(true)}>
          Scarica altro storico
        </Pulsante>
      </div>

      <Avvertenza livello="nota">
        L'elenco non è lo stato del database adesso: è una cache su disco che un cron ricalcola ogni
        due ore.{' '}
        {stato.dati?.updatedAt
          ? `Ultimo ricalcolo ${daQuanto(stato.dati.updatedAt)} (${dataOra(stato.dati.updatedAt)}), ${num(stato.dati.ranges)} periodi.`
          : 'Non è mai stato ricalcolato da quando il server è partito.'}
      </Avvertenza>

      {coppieSpezzate > 0 && (
        <Avvertenza livello="cautela">
          {coppieSpezzate}{' '}
          {coppieSpezzate === 1 ? 'coppia compare su più righe' : 'coppie compaiono su più righe'}: un
          buco di più di sessanta secondi spezza il periodo. Un backtest che attraversa il buco non
          vede quelle candele.
        </Avvertenza>
      )}

      <ContenitoreDati
        stato={schema.stato}
        causaVuoto="Non c'è nessuno storico scaricato: senza candele nessun backtest può partire."
        azioneVuoto={{ etichetta: 'Scarica il primo periodo', onClick: () => setApreScarico(true) }}
        messaggioErrore={schema.errore?.message}
        onRiprova={schema.ricarica}
        scheletro={<ScheletroLista righe={4} />}
      >
        {/* Sotto i 720px schede, da 720px la tabella. Renderizzate entrambe:
            sceglie il CSS, così ruotare il telefono non rimonta niente. */}
        <div className="sb-solo-telefono sb-colonna" style={{ gap: 'var(--sb-s-4)' }}>
          {righe.map((r) => (
            <SchedaRange key={r.id} riga={r} onElimina={() => setDaEliminare(r)} />
          ))}
        </div>

        <div className="sb-da-tablet sb-cornice" style={{ borderRadius: 'var(--sb-r-9)' }}>
          <div className="sb-scorri-x">
            <table className="sb-m-tabella">
              <thead>
                <tr>
                  <th>Coppia</th>
                  <th>Exchange</th>
                  <th>Periodo coperto</th>
                  <th>Durata</th>
                  <th>Stato</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {righe.map((r) => {
                  const f = freschezza(r.end);
                  return (
                    <tr key={r.id}>
                      <td>
                        {r.asset}/{r.currency}
                      </td>
                      <td>{r.exchange?.name ?? `#${r.exchangeId}`}</td>
                      <td>
                        {dataOra(r.begin)} → {dataOra(r.end)}
                      </td>
                      <td>{durataPeriodo(r.begin, r.end) || '—'}</td>
                      <td>
                        <Distintivo tono={f.tono}>{f.testo}</Distintivo>
                      </td>
                      <td>
                        <Pulsante variante="tenue" onClick={() => setDaEliminare(r)}>
                          Cancella…
                        </Pulsante>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </ContenitoreDati>

      <ModuloScarico
        aperto={apreScarico}
        onChiudi={() => setApreScarico(false)}
        exchangeDisponibili={listaExchange.dati?.data ?? []}
        onFatto={(messaggio) => {
          mostra(messaggio, 'riuscito');
          setApreScarico(false);
          schema.ricarica();
        }}
        onFallito={(messaggio) => mostra(messaggio, 'fallito')}
      />

      {/* La conferma ripete coppia, exchange e portata reale della cancellazione:
          il backend cancella TUTTE le candele della coppia, non solo il range
          da cui si è partiti. */}
      <Modale
        aperta={daEliminare != null}
        onChiudi={() => setDaEliminare(null)}
        titolo="Cancella lo storico"
        sottotitolo={daEliminare ? `${daEliminare.asset}/${daEliminare.currency}` : undefined}
        pieda={
          <AzioneDistruttiva
            etichetta="Cancella le candele"
            etichettaArmata="Tocca di nuovo per cancellare"
            nota="I backtest già eseguiti restano; quelli nuovi non potranno coprire questo periodo."
            onConferma={elimina}
            disabilitata={inEliminazione}
          />
        }
      >
        {daEliminare && (
          <div className="sb-colonna">
            <Scheda variante="incassata">
              <div className="sb-riga">
                <span className="sb-etichetta" style={{ flex: 1 }}>
                  Coppia
                </span>
                <span className="sb-m-voce__valore">
                  {daEliminare.asset}/{daEliminare.currency}
                </span>
              </div>
              <hr className="sb-divisore" />
              <div className="sb-riga">
                <span className="sb-etichetta" style={{ flex: 1 }}>
                  Exchange
                </span>
                <span className="sb-m-voce__valore">
                  {daEliminare.exchange?.name ?? `#${daEliminare.exchangeId}`}
                </span>
              </div>
              <hr className="sb-divisore" />
              <div className="sb-riga">
                <span className="sb-etichetta" style={{ flex: 1 }}>
                  Periodo di questa riga
                </span>
                <span className="sb-m-voce__valore">
                  {dataOra(daEliminare.begin)} → {dataOra(daEliminare.end)}
                </span>
              </div>
            </Scheda>

            <Avvertenza livello="grave">
              La cancellazione vale per TUTTE le candele di {daEliminare.asset}/
              {daEliminare.currency} su questo exchange, non solo per il periodo mostrato qui.
              Riscaricarle richiede minuti o ore.
            </Avvertenza>
          </div>
        )}
      </Modale>
    </div>
  );
}

function SchedaRange({ riga, onElimina }: { riga: RangeDatiStorici; onElimina: () => void }) {
  const f = freschezza(riga.end);
  return (
    <article className="sb-m-range">
      <div className="sb-riga" style={{ gap: 'var(--sb-s-3)' }}>
        <span style={{ fontWeight: 'var(--sb-peso-forte)', fontSize: 'var(--sb-t-135)' }}>
          {riga.asset}/{riga.currency}
        </span>
        <Distintivo contorno>{riga.exchange?.name ?? `#${riga.exchangeId}`}</Distintivo>
        <span className="sb-spazio" />
        <Distintivo tono={f.tono}>{f.testo}</Distintivo>
      </div>

      <div className="sb-nota" style={{ marginTop: 'var(--sb-s-5)', color: 'var(--sb-testo-forte)' }}>
        {dataOra(riga.begin)} → {dataOra(riga.end)}
      </div>
      <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
        {durataPeriodo(riga.begin, riga.end) || '—'} coperti · freschezza calcolata sulla fine del
        periodo
      </div>

      <div style={{ marginTop: 'var(--sb-s-5)' }}>
        <Pulsante variante="tenue" onClick={onElimina}>
          Cancella…
        </Pulsante>
      </div>
    </article>
  );
}

/* ==========================================================================
 * Scarico: sincrono, lungo, senza avanzamento
 * ======================================================================== */

function ModuloScarico({
  aperto,
  onChiudi,
  exchangeDisponibili,
  onFatto,
  onFallito,
}: {
  aperto: boolean;
  onChiudi: () => void;
  exchangeDisponibili: { id: number; name: string }[];
  onFatto: (messaggio: string) => void;
  onFallito: (messaggio: string) => void;
}) {
  const [exchangeId, setExchangeId] = useState('');
  const [asset, setAsset] = useState('');
  const [currency, setCurrency] = useState('USDT');
  const [dal, setDal] = useState('');
  const [al, setAl] = useState('');
  const [inCorso, setInCorso] = useState(false);

  const valido =
    exchangeId !== '' && asset.trim() !== '' && currency.trim() !== '' && dal !== '' && al !== '' && dal < al;

  const mesi =
    dal && al ? Math.max(1, Math.round((new Date(al).getTime() - new Date(dal).getTime()) / 2_629_800_000)) : 0;

  async function invia() {
    if (!valido) return;
    setInCorso(true);
    try {
      await datiStorici.scarica({
        exchangeId: Number(exchangeId),
        asset: asset.trim().toUpperCase(),
        currency: currency.trim().toUpperCase(),
        startDate: dal,
        endDate: al,
      });
      onFatto(`Storico di ${asset.trim().toUpperCase()}/${currency.trim().toUpperCase()} scaricato.`);
    } catch (e) {
      onFallito(e instanceof ErroreApi ? e.message : 'Scarico non riuscito.');
    } finally {
      setInCorso(false);
    }
  }

  return (
    <Modale
      aperta={aperto}
      onChiudi={onChiudi}
      titolo="Scarica storico"
      sottotitolo="candele da un minuto, in sequenza"
      pieda={
        <Pulsante variante="primario" largo onClick={invia} disabled={!valido || inCorso}>
          {inCorso ? 'Scarico in corso: non chiudere…' : 'Avvia lo scarico'}
        </Pulsante>
      }
    >
      <div className="sb-colonna">
        <Avvertenza livello="cautela">
          La richiesta è sincrona e non ha avanzamento: resta appesa finché il server non ha
          finito. Su anni di storia possono volerci molti minuti, e chiudere la finestra non ferma
          lo scarico.
        </Avvertenza>

        <Selettore
          etichetta="Exchange"
          valore={exchangeId}
          voci={[
            { valore: '', etichetta: 'scegli…' },
            ...exchangeDisponibili.map((e) => ({ valore: String(e.id), etichetta: e.name })),
          ]}
          onCambia={setExchangeId}
        />

        <div className="sb-griglia sb-griglia--stretta">
          <Campo
            etichetta="Asset"
            testuale
            suggerimento="es. BTC"
            value={asset}
            onChange={(e) => setAsset(e.target.value)}
            autoComplete="off"
          />
          <Campo
            etichetta="Valuta"
            testuale
            suggerimento="es. USDT"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            autoComplete="off"
          />
        </div>

        <div className="sb-griglia sb-griglia--stretta">
          <Campo etichetta="Dal" type="date" value={dal} onChange={(e) => setDal(e.target.value)} />
          <Campo etichetta="Al" type="date" value={al} onChange={(e) => setAl(e.target.value)} />
        </div>

        {dal !== '' && al !== '' && dal >= al && (
          <Avvertenza livello="grave">La data di fine deve venire dopo quella di inizio.</Avvertenza>
        )}

        {valido && (
          <Avvertenza livello="nota">
            Circa {mesi} {mesi === 1 ? 'mese' : 'mesi'} di candele da un minuto: sono all'incirca{' '}
            {num(mesi * 43_800)} righe da scaricare una pagina per volta.
          </Avvertenza>
        )}
      </div>
    </Modale>
  );
}
